import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Trash2, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ActionTabs } from "@/components/monitor/ActionTabs";
import { KpiCards } from "@/components/monitor/KpiCards";
import { FilterBar } from "@/components/monitor/FilterBar";
import { LogTable } from "@/components/monitor/LogTable";
import { LogDetailPanel } from "@/components/monitor/LogDetailPanel";
import { MailDialog } from "@/components/monitor/MailDialog";
import { DEFAULT_FILTERS, type MonitorFilters } from "@/components/monitor/types";
import { useLogStream } from "@/hooks/useLogStream";
import { api, HttpError } from "@/lib/api";
import { fmt, questionText } from "@/lib/utils";
import type { LogDeleteResponse, LogDetail, TabKey } from "@/lib/types";

export function MonitorPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState<MonitorFilters>(DEFAULT_FILTERS);
  const [selectedQid, setSelectedQid] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  /** 메일 발송 다이얼로그 대상 질의 — null 이면 닫힘. */
  const [mailTarget, setMailTarget] = useState<LogDetail | null>(null);

  const { items, total, loading, refreshing, paused, error, reload } =
    useLogStream(filters);

  // 필터가 바뀌면 화면에서 사라진 행의 선택은 의미가 없다 — 현재 목록에 있는 것만 남긴다.
  const visibleIds = useMemo(
    () => new Set(items.map((it) => it.query_id)),
    [items],
  );
  const checkedVisible = useMemo(
    () => [...checked].filter((qid) => visibleIds.has(qid)),
    [checked, visibleIds],
  );

  const clearSelection = useCallback(() => setChecked(new Set()), []);

  const toggleSelect = useCallback((queryId: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(queryId)) next.delete(queryId);
      else next.add(queryId);
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (on: boolean) => {
      setChecked(on ? new Set(items.map((it) => it.query_id)) : new Set());
    },
    [items],
  );

  /** 삭제/복원 공통 실행 — 완료 후 선택 해제 + 목록 재조회. */
  const run = useCallback(
    async (fn: () => Promise<LogDeleteResponse>, verb: "삭제" | "복원") => {
      setBusy(true);
      try {
        const res = await fn();
        clearSelection();
        setSelectedQid(null);
        // 상세 캐시는 deleted 플래그를 품고 있어 삭제/복원 뒤 그대로 두면 어긋난다.
        qc.invalidateQueries({ queryKey: ["log-detail"] });
        await reload();
        if (res.skipped > 0) {
          window.alert(
            `${res.affected}건 ${verb}했습니다. ` +
              `${res.skipped}건은 이미 ${verb}되었거나 원본이 없어 건너뛰었습니다.`,
          );
        }
      } catch (e) {
        window.alert(
          e instanceof HttpError
            ? `${verb}에 실패했습니다: ${e.message}`
            : `${verb}에 실패했습니다.`,
        );
      } finally {
        setBusy(false);
      }
    },
    [clearSelection, qc, reload],
  );

  /** 단건 삭제 — label 은 확인 문구에 보여줄 요청 문구. */
  const deleteOne = useCallback(
    (queryId: string, label: string) => {
      if (!window.confirm(`이 이력을 삭제할까요?\n\n${label}`)) return;
      void run(() => api.delete<LogDeleteResponse>(`/logs/${queryId}`), "삭제");
    },
    [run],
  );

  const restoreOne = useCallback(
    (queryId: string) =>
      void run(
        () =>
          api.post<LogDeleteResponse>("/logs/restore", { query_ids: [queryId] }),
        "복원",
      ),
    [run],
  );

  const deleteSelected = useCallback(() => {
    if (checkedVisible.length === 0) return;
    if (
      !window.confirm(
        `선택한 ${checkedVisible.length}건의 이력을 삭제할까요?\n\n` +
          "목록·상세·CSV 에서 숨겨집니다. '삭제된 이력 보기'에서 복원할 수 있습니다.",
      )
    )
      return;
    void run(
      () =>
        api.post<LogDeleteResponse>("/logs/delete", {
          query_ids: checkedVisible,
        }),
      "삭제",
    );
  }, [checkedVisible, run]);

  const restoreSelected = useCallback(() => {
    if (checkedVisible.length === 0) return;
    void run(
      () =>
        api.post<LogDeleteResponse>("/logs/restore", {
          query_ids: checkedVisible,
        }),
      "복원",
    );
  }, [checkedVisible, run]);

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold">실시간 모니터링</h1>
          <p className="text-xs text-muted-foreground">
            10초 주기 자동 갱신 (탭 비활성 시 일시정지)
          </p>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <div>표시 {fmt(items.length)} / 총 {fmt(total)}</div>
          {refreshing && <div className="text-primary">갱신 중…</div>}
        </div>
      </header>

      <KpiCards />

      <ActionTabs
        value={filters.tab}
        onChange={(tab: TabKey) => setFilters({ ...filters, tab })}
      />

      <FilterBar
        value={filters}
        onChange={setFilters}
        onRefresh={reload}
        refreshing={refreshing}
        paused={paused}
      />

      {error && (
        <Alert variant="destructive">
          <AlertTitle>로그 조회 실패</AlertTitle>
          <AlertDescription>
            {error.message}
            {error.code ? ` (${error.code})` : ""}
          </AlertDescription>
        </Alert>
      )}

      {checkedVisible.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <span className="font-medium">{fmt(checkedVisible.length)}건 선택됨</span>
          <Button
            variant="destructive"
            size="sm"
            className="gap-1"
            disabled={busy}
            onClick={deleteSelected}
          >
            <Trash2 className="h-4 w-4" />
            선택 삭제
          </Button>
          {filters.include_deleted && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={busy}
              onClick={restoreSelected}
            >
              <RotateCcw className="h-4 w-4" />
              선택 복원
            </Button>
          )}
          <Button variant="ghost" size="sm" className="gap-1" onClick={clearSelection}>
            <X className="h-4 w-4" />
            선택 해제
          </Button>
          <span className="text-xs text-muted-foreground">
            삭제해도 원본 로그는 보존되며, 통계·보고서 수치는 변하지 않습니다.
          </span>
        </div>
      )}

      <LogTable
        items={items}
        loading={loading}
        slowThresholdMs={filters.slow_threshold_ms}
        selectedId={selectedQid ?? undefined}
        onRowClick={(it) => setSelectedQid(it.query_id)}
        selectedIds={checked}
        onToggleSelect={toggleSelect}
        onToggleAll={toggleAll}
        onDelete={(it) =>
          deleteOne(it.query_id, questionText(it.question_summary, it.files))
        }
        onRestore={(it) => restoreOne(it.query_id)}
      />

      <LogDetailPanel
        queryId={selectedQid}
        includeDeleted={filters.include_deleted}
        onClose={() => setSelectedQid(null)}
        onDelete={(d) =>
          deleteOne(d.query_id, questionText(d.question ?? "", d.files))
        }
        onRestore={(d) => restoreOne(d.query_id)}
        onSendMail={(d) => setMailTarget(d)}
      />

      <MailDialog detail={mailTarget} onClose={() => setMailTarget(null)} />
    </div>
  );
}
